import { WHITE_LABEL_CONFIG } from "@/whiteLable";
import {
  Activity,
  BarChart3,
  BookOpen,
  ClipboardCheck,
  Cog,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  MessageSquare,
  Settings,
  Share2,
  ShieldCheck,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { App_Url_New } from "../common/static";
import { NavChild, NavItemType } from "../types/header";

export const B2C_FEATURE_FLAGS = {
  // When true, insurer selectors/panels are hidden for OC environment behavior.
  isOCEnvironment: true,
  // Toggle product visibility across B2C journeys.
  isCarHide: false,
  isHomeHide: false,
  isTravelHide: false,
  isHealthHide: false,
  isLifeHide: true,
  // remove validation and direct jump to next step
  applyValidation: false,
  // Toggle life-focused content for the homepage and navigation.
  isLifeHomeContent: false,
} as const;

export type B2CProductKey = "motor" | "home" | "travel" | "health" | "life";

const PRODUCT_HIDE_FLAGS: Record<B2CProductKey, boolean> = {
  motor: B2C_FEATURE_FLAGS.isCarHide,
  home: B2C_FEATURE_FLAGS.isHomeHide,
  travel: B2C_FEATURE_FLAGS.isTravelHide,
  health: B2C_FEATURE_FLAGS.isHealthHide,
  life: B2C_FEATURE_FLAGS.isLifeHide,
};

export const isB2CProductVisible = (product: B2CProductKey) => !PRODUCT_HIDE_FLAGS[product];

// Life-focused content: swaps the whole product menu for life sub-products.
// Only applies while the Life product itself is visible.
export const isLifeHomeContentEnabled = () =>
  isB2CProductVisible("life") && B2C_FEATURE_FLAGS.isLifeHomeContent;

export const PRODUCT_NAV_LABEL = isLifeHomeContentEnabled() ? "Products" : "Insurance";

const LIFE_HOME_NAV_CHILDREN: NavChild[] = [
  { label: "Term Life", path: App_Url_New.D2CPageUrl.insurance.lifeGetQuote },
  // No journey for these two yet, so they stay on the home page.
  { label: "Market Linked Products", path: App_Url_New.D2CPageUrl.home },
  { label: "Income Products", path: App_Url_New.D2CPageUrl.home },
];

const STANDARD_NAV_CHILDREN: NavChild[] = [
  ...(isB2CProductVisible("motor")
    ? [{ label: "Car", path: App_Url_New.D2CPageUrl.insurance.carGetQuote }]
    : []),
  ...(isB2CProductVisible("home")
    ? [{ label: "Home", path: App_Url_New.D2CPageUrl.insurance.home }]
    : []),
  ...(isB2CProductVisible("travel")
    ? [{ label: "Travel", path: App_Url_New.D2CPageUrl.insurance.travelGetQuote }]
    : []),
  ...(isB2CProductVisible("health")
    ? [{ label: "Health", path: App_Url_New.D2CPageUrl.insurance.healthGetQuote }]
    : []),
  ...(isB2CProductVisible("life")
    ? [{ label: "Life", path: App_Url_New.D2CPageUrl.insurance.lifeGetQuote }]
    : []),
];

const PRODUCT_NAV_CHILDREN = isLifeHomeContentEnabled()
  ? LIFE_HOME_NAV_CHILDREN
  : STANDARD_NAV_CHILDREN;

// Use this instead of looking the menu up by label — the label itself is dynamic.
export const getProductNavChildren = () => PRODUCT_NAV_CHILDREN;

const SERVICE_NAV_CHILDREN: NavChild[] = [
  { label: "Claims", path: App_Url_New.CommonPageUrl.services.claims },

  ...(isLifeHomeContentEnabled()
    ? []
    : [
        {
          label: "Roadside Assistance",
          path: App_Url_New.CommonPageUrl.services.roadsideAssistance,
        },
      ]),
  { label: "Complaints", path: App_Url_New.CommonPageUrl.services.complaints },
];

export const getServiceNavChildren = () => SERVICE_NAV_CHILDREN;

export const HOMEPAGE_CONTENT = (() => {
  const { lifeContent, ...baseHomepage } = WHITE_LABEL_CONFIG.homepage;

  if (!isLifeHomeContentEnabled() || !lifeContent) {
    return baseHomepage;
  }

  return Object.entries(lifeContent).reduce(
    (merged, [section, overrides]) => ({
      ...merged,
      [section]: { ...merged[section], ...(overrides as object) },
    }),
    baseHomepage
  );
})();

export const HEADER_CONFIG: {
  brand: {
    link: string;
  };
  phone: {
    label: string;
    value: string;
  };
  navigation: NavItemType[];
} = {
  brand: {
    link: App_Url_New.D2CPageUrl.home,
  },

  phone: {
    label: WHITE_LABEL_CONFIG.contact.phoneLabel,
    value: WHITE_LABEL_CONFIG.contact.phoneValue,
  },

  navigation: [
    {
      label: PRODUCT_NAV_LABEL,
      children: PRODUCT_NAV_CHILDREN,
    },
    {
      label: "Services",
      children: SERVICE_NAV_CHILDREN,
    },
    {
      label: "About Us",
      path: App_Url_New.CommonPageUrl.aboutUs,
    },
  ],
};

export const SecureNavBarItems = [
  {
    id: "Dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    module: "dashboard",
    path: App_Url_New.B2CPageUrl.dashboard,
    action: "view",
  },
  {
    id: "Pipeline",
    label: "Pipeline",
    icon: BarChart3,
    module: "pipeline",
    path: App_Url_New.B2CPageUrl.pipeline,
    action: "view",
  },
  {
    id: "Client",
    label: "Clients",
    icon: Users,
    module: "clients",
    path: App_Url_New.B2CPageUrl.clients,
    action: "list",
  },
  {
    id: "Calls",
    label: "Calls",
    icon: MessageSquare,
    module: "calls",
    path: App_Url_New.B2CPageUrl.calls,
    action: "view",
  },
  {
    id: "Quotes",
    label: "Quotes",
    icon: FileText,
    module: "quotes",
    path: App_Url_New.B2CPageUrl.quotes,
    action: "view",
  },
  {
    id: "Policies",
    label: "Policies",
    icon: ShieldCheck,
    module: "policies", // Make sure this matches your backend RBAC permission module
    path: App_Url_New.B2CPageUrl.policies, // You will need to add this to your static.ts
    action: "view", // Make sure this matches your backend RBAC action
  },
  {
    id: "Claims",
    label: "Claims",
    icon: ClipboardCheck,
    module: "claims",
    path: App_Url_New.B2CPageUrl.claims,
    action: "view",
  },
  {
    id: "Cases",
    label: "Cases",
    icon: LifeBuoy,
    module: "case",
    path: App_Url_New.B2CPageUrl.cases,
    action: "view",
  },
  // {
  //   id: "Commission Dashboard",
  //   label: "Commission Dashboard",
  //   icon: Landmark,
  //   module: "dashboard",
  //   path: App_Url_New.B2CPageUrl.commissionDashboard,
  //   action: "view",
  // },
  {
    id: "Commission Ledger",
    label: "Commission Ledger",
    icon: BookOpen,
    module: "commission-ledger",
    path: App_Url_New.B2CPageUrl.commissionLedger,
    action: "view",
  },
  {
    id: "Invoices",
    label: "Invoices",
    icon: FileText,
    module: "invoices",
    path: App_Url_New.B2CPageUrl.invoices,
    action: "view",
  },

  {
    id: "IRDAI Compliance",
    label: "IRDAI Compliance",
    icon: ShieldCheck,
    module: "irdai-compliance",
    path: App_Url_New.B2CPageUrl.irdaiReports,
    action: "view",
  },
  {
    id: "Settings",
    label: "Settings",
    icon: Settings,
    module: "settings",
    path: App_Url_New.B2CPageUrl.settings,
    action: "view",
  },
  {
    id: "Third Party API",
    label: "API Monitoring",
    icon: Activity,
    module: "api-monitoring",
    path: App_Url_New.AdminPageUrl.thirdPartyApi,
    action: "list",
  },
  {
    id: "insurer",
    label: "Insurer",
    icon: UserRoundCheck,
    path: App_Url_New.AdminPageUrl.insurer,
    module: "insurerconfig",
    action: "view",
  },
  {
    id: "Channels",
    label: "Channels",
    icon: Share2,
    module: "channels",
    path: App_Url_New.AdminPageUrl.channels,
    action: "list",
  },
  {
    id: "Review queue",
    label: "Review Queue",
    icon: ListChecks,
    module: "review-queue",
    path: App_Url_New.AdminPageUrl.reviewQueue,
    action: "list",
  },
  {
    id: "API Configuration",
    label: "Api Configuration",
    icon: Cog,
    module: "apiconfig",
    path: App_Url_New.AdminPageUrl.apiConfiguration,
    action: "list",
  },
  {
    id: "Logs",
    label: "Logs",
    icon: FileText,
    module: "logs",
    path: App_Url_New.AdminPageUrl.logs,
    action: "view",
  },
];
